import {
    nextTick, onBeforeUnmount, ref, type Ref, watch,
} from 'vue';
import { TABLE_COLUMN_TRACKS } from '@/helpers/table/tableGridPlacement.ts';

/** Capture resolved tracks after loading/layout changes, never after ordinary row updates. */
export function useFixedTableColumns (
    table: Ref<HTMLTableElement | null>,
    ready: Ref<boolean>,
): {
        columns: Readonly<Ref<string | null>>;
        resetColumns: () => Promise<void>;
        refitColumns: (columnStarts: number[]) => Promise<void>;
    } {
    const columns = ref<string | null>(null);
    let fixedTracks: string[] | null = null;
    const pendingColumns = new Set<number>();
    let generation = 0;
    let observer: ResizeObserver | undefined;
    let observedTable: HTMLTableElement | null = null;

    async function capture (element: HTMLTableElement, currentGeneration: number): Promise<void> {
        // First let Vue restore the requested intrinsic tracks, then wait for fonts.
        await nextTick();
        await document.fonts.ready;
        if (currentGeneration !== generation) return;
        if (element.clientWidth === 0) return;

        const resolved = getComputedStyle(element).gridTemplateColumns;
        // Hidden tables and non-layout environments can return an unresolved template.
        if (/^(?:\d+(?:\.\d+)?px\s*)+$/.test(resolved)) {
            columns.value = resolved;
            fixedTracks = resolved.trim().split(/\s+/);
            pendingColumns.clear();
        }
    }

    async function fit (): Promise<void> {
        const currentGeneration = ++generation;
        const element = table.value;
        columns.value = null;
        fixedTracks = null;
        pendingColumns.clear();
        if (!element || !ready.value) return;
        await capture(element, currentGeneration);
    }

    async function refitColumns (columnStarts: number[]): Promise<void> {
        const element = table.value;
        // An initial/full fit already includes any heading changes made while pending.
        if (!element || !ready.value || !fixedTracks || !columnStarts.length) return;

        columnStarts.forEach(start => pendingColumns.add(start));
        const tracks = [...fixedTracks];
        pendingColumns.forEach(start => {
            TABLE_COLUMN_TRACKS.forEach((track, offset) => { tracks[start - 1 + offset] = track; });
        });
        const currentGeneration = ++generation;
        columns.value = tracks.join(' ');
        await capture(element, currentGeneration);
    }

    watch([table, ready], () => {
        if (table.value !== observedTable) {
            observer?.disconnect();
            observedTable = table.value;
            if (observedTable) {
                let lastWidth: number | undefined;
                observer = new ResizeObserver(entries => {
                    const width = entries[0].contentRect.width;
                    // Ignore height changes; an unfitted hidden table may first notify on reveal.
                    if (width !== lastWidth && (lastWidth !== undefined || !columns.value)) fit();
                    lastWidth = width;
                });
                observer.observe(observedTable.parentElement!);
            }
        }
        fit();
    }, { flush: 'post' });

    onBeforeUnmount(() => {
        generation += 1;
        observer?.disconnect();
    });

    return { columns, resetColumns: fit, refitColumns };
}
